import type {TFunction} from 'i18next';

/** Parses "M-YYYY" or "YYYY-M" period keys into a comparable {year, month}. */
export function parsePeriodKey(periodKey: string): { year: number; month: number } | null {
    const parts = periodKey.split('-');
    if (parts.length < 2) return null;
    let month: number;
    let year: number;
    if (parts[0].length === 4) {
        year = Number.parseInt(parts[0], 10);
        month = Number.parseInt(parts[1], 10);
    } else {
        month = Number.parseInt(parts[0], 10);
        year = Number.parseInt(parts[1], 10);
    }
    if (Number.isNaN(month) || Number.isNaN(year) || month < 1 || month > 12) return null;
    return {year, month};
}

/**
 * Human-readable, translated period label (e.g. "Gusht 2026") for the raw API period, which
 * looks like "2026-8 (Status: OPEN)". Falls back to the raw value when it can't be parsed.
 */
export function formatPeriodLabel(
    t: TFunction,
    periodKey: string | null | undefined,
    rawValue?: string | null,
): string {
    // The key is authoritative; the raw value ("2026-8 (Status: OPEN)") is only a fallback source.
    const parsed = (periodKey ? parsePeriodKey(periodKey) : null)
        ?? (rawValue ? parsePeriodKey(rawValue.split(/[\s(]/)[0]) : null);
    if (!parsed) return rawValue ?? '';
    return `${t(`common.monthFull.${parsed.month}`)} ${parsed.year}`;
}

/**
 * Month-only label (e.g. "Gusht"), for UIs that already show the year in an adjacent control
 * (e.g. a separate year dropdown) and would otherwise duplicate it.
 */
export function formatMonthLabel(
    t: TFunction,
    periodKey: string | null | undefined,
    rawValue?: string | null,
): string {
    const parsed = (periodKey ? parsePeriodKey(periodKey) : null)
        ?? (rawValue ? parsePeriodKey(rawValue.split(/[\s(]/)[0]) : null);
    if (!parsed) return rawValue ?? '';
    return t(`common.monthFull.${parsed.month}`);
}
